import {
  AirPricingRecords,
  Amount,
  ErrorMessage,
  JourneyUnpaidBaggageItem,
  JourneyUnpaidSeatItem,
  Price,
  ServiceItem,
} from '@dapi/sdk';
import {
  createJourneyUnpaidItems,
  generateId,
  MockJourneyUnpaidItems,
} from '@dapi/sdk/spec';
import {
  checkInFlight,
  currency,
  filledAdTChdInfJourneyTravelers,
  journeyTraveler,
  journeyTraveler2,
} from '../dictionaries';
import {
  multiLegJourneys,
  roundTripJourneys,
} from './mock-journeys';
import {
  firstCHSeatOnJourney,
  firstCHSeatOnMultiLegJourney,
  firstCHSeatOnRoundTripJourney,
  secondCHSeatOnJourney,
} from './mock-seatmap';

export const noItemToPayError: ErrorMessage = {
  code: '10000',
  title: 'No unpaid service',
  type: 'error',
  id: '10000'
};

export const price = 6000;
export const priceCurrCode = 'EUR';
export const priceCurrName = 'Euro';

const amount: Amount = {
  currencyCode: 'EUR',
  value: 6000,
  currency: currency
};

const totalAmount: Amount = {
  currencyCode: 'EUR',
  value: 130_000,
  currency: currency
};

const unpaidSeatAmount = {
  currencyCode: 'EUR',
  value: 500,
  currency: currency
};

const twoJourneyUnpaidBaggageItem: JourneyUnpaidBaggageItem = {
  travelerId: journeyTraveler.id,
  quantity: 2,
  quantityUnit: 'pieces',
  baggageType: '',
  flightIds: [checkInFlight.id],
  amount: amount,
  traveler: journeyTraveler,
  id: generateId('JourneyUnpaidBaggageItem', '0')
};

const journeyUnpaidSeatItem: JourneyUnpaidSeatItem = {
  travelerId: journeyTraveler.id,
  seatNumber: firstCHSeatOnJourney[0].seatNumber || '',
  flightId: checkInFlight.id,
  amount: unpaidSeatAmount,
  traveler: journeyTraveler,
  id: generateId('JourneyUnpaidSeatItem', '0'),
  flight: checkInFlight
};

const multiLegTravelers = multiLegJourneys[0].travelers;
const multiLegCheckinFlight = multiLegJourneys[0].flights[0].checkInFlight;
const multiLegJourneyUnpaidSeatItem: JourneyUnpaidSeatItem = {
  travelerId: multiLegTravelers[0].id || '',
  seatNumber: firstCHSeatOnMultiLegJourney[0].seatNumber || '',
  flightId: multiLegCheckinFlight.id,
  amount: unpaidSeatAmount,
  traveler: multiLegTravelers[0],
  id: generateId('JourneyUnpaidSeatItem', '0'),
  flight: multiLegCheckinFlight
};

const roundTripTravelers = roundTripJourneys[2].travelers;
const roundTripCheckinFlight = roundTripJourneys[2].flights[0].checkInFlight;
const roundTripJourneyAdultUnpaidSeatItem: JourneyUnpaidSeatItem = {
  travelerId: roundTripTravelers[0].id || '',
  seatNumber: firstCHSeatOnRoundTripJourney[0].seatNumber || '',
  flightId: roundTripCheckinFlight.id,
  amount: unpaidSeatAmount,
  traveler: roundTripTravelers[0],
  id: generateId('JourneyUnpaidSeatItem', '0'),
  flight: roundTripCheckinFlight
};

const journeyUnPaidSeatForSecondPax: JourneyUnpaidSeatItem = {
  ...journeyUnpaidSeatItem,
  seatNumber: secondCHSeatOnJourney[0].seatNumber || '',
  travelerId: filledAdTChdInfJourneyTravelers[1].id,
  traveler: journeyTraveler2
};

const totalPrices: Price = {
  currencyCode: 'EUR',
  total: 5000,
  currency: currency,
  totalTaxes: 0
};

const unpaidServiesPrices: AirPricingRecords = {
  totalPrices: [totalPrices]
};

const unpaidPriorityBoardingServices3: ServiceItem = {
  descriptions: [
    {
      content: 'PBRD',
      type: 'shortText'
    }],
  travelerId: 'JOURNEY-TRAVELER-0-MOCK',
  quantity: 1,
  flightIds: ['CHECK-IN-FLIGHT-0-MOCK'],
  prices: unpaidServiesPrices,
  id: generateId('JourneyUnpaidService', '0')
};

const unpaidPriorityBoardingServices4: ServiceItem = {
  descriptions: [
    {
      content: 'PBRD',
      type: 'shortText'
    }],
  travelerId: 'JOURNEY-TRAVELER-1-MOCK',
  quantity: 1,
  flightIds: ['CHECK-IN-FLIGHT-0-MOCK'],
  prices: unpaidServiesPrices,
  id: generateId('JourneyUnpaidService', '1')
};

const UnpaidBaggage1Item1: JourneyUnpaidBaggageItem = {
  travelerId: 'JOURNEY-TRAVELER-0-MOCK',
  quantity: 1,
  quantityUnit: 'pieces',
  baggageType: '',
  flightIds: ['CHECK-IN-FLIGHT-0-MOCK'],
  amount: amount,
  traveler: journeyTraveler,
  id: generateId('JourneyUnpaidBaggageItem', '0')
};

const UnpaidBaggage1Item2: JourneyUnpaidBaggageItem = {
  travelerId: 'JOURNEY-TRAVELER-1-MOCK',
  quantity: 1,
  quantityUnit: 'pieces',
  baggageType: '',
  flightIds: ['CHECK-IN-FLIGHT-0-MOCK'],
  amount: amount,
  traveler: journeyTraveler2,
  id: generateId('JourneyUnpaidBaggageItem', '1')
};

const UnpaidBaggage1Item3: JourneyUnpaidBaggageItem = {
  travelerId: 'JOURNEY-TRAVELER-2-MOCK',
  quantity: 1,
  quantityUnit: 'pieces',
  baggageType: '',
  flightIds: ['CHECK-IN-FLIGHT-0-MOCK'],
  amount: amount,
  traveler: journeyTraveler2,
  id: generateId('JourneyUnpaidBaggageItem', '1')
};

const unpaidServiceAmount = {
  currencyCode: 'EUR',
  value: 500,
  currency: currency
};

export const baggageToPay: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    baggage: [twoJourneyUnpaidBaggageItem],
    total: amount
  }
});

export const seatsToPay: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    seats: [journeyUnpaidSeatItem],
    total: unpaidSeatAmount
  }
});

export const chargeableSeatsToPayForTwoPax: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    seats: [journeyUnpaidSeatItem, journeyUnPaidSeatForSecondPax],
    total: unpaidSeatAmount
  }
});

export const chargeableSeatsToPayForMultiLegJourney: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    seats: [multiLegJourneyUnpaidSeatItem],
    total: unpaidSeatAmount
  }
});

export const chargeableSeatsToPayForRoundTripJourney: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    seats: [roundTripJourneyAdultUnpaidSeatItem],
    total: unpaidSeatAmount
  }
});

export const servicesToPay: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    services: [unpaidPriorityBoardingServices3, unpaidPriorityBoardingServices4],
    total: unpaidServiceAmount
  }
});

export const servicesToPayForOnePax: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    services: [unpaidPriorityBoardingServices3],
    total: unpaidServiceAmount
  }
});

export const baggageToPayfortwopax: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    seats: [journeyUnpaidSeatItem, journeyUnPaidSeatForSecondPax],
    baggage: [UnpaidBaggage1Item1, UnpaidBaggage1Item2],
    services: [unpaidPriorityBoardingServices3, unpaidPriorityBoardingServices4],
    total: totalAmount
  }
});

export const baggageAndServicesToPayforthreepax: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    seats: [journeyUnpaidSeatItem, journeyUnPaidSeatForSecondPax],
    baggage: [UnpaidBaggage1Item1, UnpaidBaggage1Item2, UnpaidBaggage1Item3],
    services: [unpaidPriorityBoardingServices3, unpaidPriorityBoardingServices4],
    total: totalAmount
  }
});

export const seatbagsandservicesToPay: MockJourneyUnpaidItems = createJourneyUnpaidItems({
  template: {
    seats: [journeyUnpaidSeatItem],
    baggage: [twoJourneyUnpaidBaggageItem],
    services: [unpaidPriorityBoardingServices3],
    total: unpaidServiceAmount
  }
});
